// Google consent for Calendar and Drive. Web and phones use a popup or an auth
// session. Desktop can't: Google bars sign-in in a window an app controls, so it
// runs in the OS browser and returns on a URL scheme any app on the computer can
// claim. The claim that comes back is only good with the verifier kept here,
// which the server checks (backend services/google/desktop-consent.js).

import config from '../config';
import { openOAuthSession, type OAuthResult } from './oauthBrowser';
import { createPkce } from './pkce';
import { connectsThroughBrowser } from './platform';

export interface GoogleConsentResult extends OAuthResult {
  /** Desktop only. The server requires it to redeem the claim. */
  claimVerifier?: string;
}

export async function openGoogleConsent(params: URLSearchParams): Promise<GoogleConsentResult> {
  const scheme = config.google.appRedirectScheme;
  const authUrl = () => `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  if (!connectsThroughBrowser()) return openOAuthSession(authUrl(), scheme, { preferEphemeralSession: true });

  // Lazy, so the screens that import this don't load the API client with it.
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { DIRECT_CONNECT_CHANNEL } = require('./directConnect');
  const pkce = await createPkce();
  params.set('state', `desktop.${pkce.state}.${pkce.challenge}`);
  const result = await openOAuthSession(authUrl(), scheme, {
    bridgeChannel: DIRECT_CONNECT_CHANNEL,
    expectedState: pkce.state,
    systemBrowser: true,
  });
  if (result.type !== 'error' || !result.url) return { ...result, claimVerifier: pkce.verifier };

  // A refusal reads the way it does on a phone, so callers' error handling runs.
  const reason = new URL(result.url).searchParams.get('reason') || 'unknown';
  return { type: 'success', url: `${scheme}?error=${encodeURIComponent(reason)}` };
}
